// Copyright (c) Microsoft Corporation.
// Licensed under the MIT License.

import {
    Menu,
    MenuButton,
    MenuDivider,
    MenuItem,
    MenuItemCheckbox,
    MenuList,
    MenuPopover,
    MenuTrigger,
    Tag,
} from '@fluentui/react-components';
import { FilterDismissRegular } from '@fluentui/react-icons';
import React, { useCallback } from 'react';
import { FormattedMessage } from 'react-intl';
import { useAppStyles } from '../../styles';

// Filter pill control properties.
export type FilterPillProps = Readonly<{
    title: string | JSX.Element;
    options: [string, string][];
    onChanged: (value: string[]) => void;
}>;

// Filter pill control.
export const FilterPill = (props: FilterPillProps) => {
    const { title, options, onChanged } = props;
    const styles = useAppStyles();
    const [checked, setChecked] = React.useState<{ [key: string]: string[] }>({});

    const onCheckedValueChange = useCallback(
        (checkedItems: string[]) => {
            onChanged(checkedItems);
            setChecked({ value: checkedItems });
        },
        [onChanged]
    );

    const onClearClick = useCallback(() => {
        onChanged([]);
        setChecked({});
    }, [onChanged]);

    return (
        <Menu checkedValues={checked} onCheckedValueChange={(_, data) => onCheckedValueChange(data.checkedItems)}>
            <MenuTrigger disableButtonEnhancement>
                <MenuButton shape="circular">
                    <div className={styles.flexRowSmallGapCentered}>
                        <span>{title}</span>
                        {checked.value && checked.value.length > 0 && (
                            <Tag className={styles.counterTag} shape="circular" size="extra-small">
                                {checked.value.length}
                            </Tag>
                        )}
                    </div>
                </MenuButton>
            </MenuTrigger>
            <MenuPopover>
                <MenuList>
                    {options.map(([key, value]) => (
                        <MenuItemCheckbox key={key} name={'value'} value={key}>
                            {value}
                        </MenuItemCheckbox>
                    ))}
                    <MenuDivider />
                    <MenuItem icon={<FilterDismissRegular />} onClick={onClearClick}>
                        <FormattedMessage defaultMessage="Clear" description="Clear filter menu item" />
                    </MenuItem>
                </MenuList>
            </MenuPopover>
        </Menu>
    );
};
